import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, Search, FileText, Printer, Mail, Edit, Eye, AlertCircle, AlertTriangle, Check, MessageSquare, Phone
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';

// Print styles for professional patient handouts
const printStyles = `
  @media screen {
    .print-only {
      display: none !important;
    }
  }
  
  @media print {
    @page {
      margin: 0.5in;
      size: letter portrait;
    }
    
    /* Hide everything on screen */
    body > *:not(.print-only) {
      display: none !important;
    }
    
    /* Show only print content */
    .print-only {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
    }
    
    /* Ensure colors print */
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* Page breaks */
    .print-header, .border-l-4 {
      page-break-inside: avoid !important;
    }
  }
`;

const MedCardLibraryPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { practice, user } = useAuth();
  const [medCards, setMedCards] = useState([]);
  const [filteredMedCards, setFilteredMedCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  
  // Disclaimer state
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [signature, setSignature] = useState('');
  const [checkingDisclaimer, setCheckingDisclaimer] = useState(true);
  const [disclaimerLoading, setDisclaimerLoading] = useState(false);
  
  // View/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  
  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [emailingCard, setEmailingCard] = useState(null);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  
  // SMS modal
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsCard, setSmsCard] = useState(null);
  const [smsPatient, setSmsPatient] = useState(null);
  const [smsPatientSearchQuery, setSmsPatientSearchQuery] = useState('');
  const [filteredSmsPatients, setFilteredSmsPatients] = useState([]);
  const [sendingSMS, setSendingSMS] = useState(false);

  useEffect(() => {
    checkDisclaimerStatus();
  }, []);

  useEffect(() => {
    if (disclaimerChecked) {
      loadMedCards();
      loadPatients();
    }
  }, [disclaimerChecked]);

  useEffect(() => {
    filterMedCards();
  }, [searchQuery, selectedCategory, medCards]);

  useEffect(() => {
    filterPatients();
  }, [patientSearchQuery, patients]);

  // Filter patients for SMS modal - only show patients with phone numbers
  useEffect(() => {
    let filtered = [...patients];

    // First filter to only patients with phone numbers
    filtered = filtered.filter(patient => patient.cellPhone || patient.phone);

    if (smsPatientSearchQuery.trim()) {
      const searchLower = smsPatientSearchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
        (patient.email && patient.email.toLowerCase().includes(searchLower))
      );
    }

    filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });

    setFilteredSmsPatients(filtered);
  }, [smsPatientSearchQuery, patients]);

  const checkDisclaimerStatus = async () => {
    try {
      const data = await practiceApi.getMedCardDisclaimerStatus();
      if (data.success && data.accepted) {
        setDisclaimerChecked(true);
      } else {
        setShowDisclaimer(true);
      }
    } catch (error) {
      console.error('Error checking disclaimer:', error);
      setShowDisclaimer(true);
    } finally {
      setCheckingDisclaimer(false);
    }
  };

  const handleAcceptDisclaimer = async () => {
    if (!signature.trim()) {
      toast({
        title: "Signature Required",
        description: "Please provide your signature",
        variant: "destructive"
      });
      return;
    }

    try {
      setDisclaimerLoading(true);
      const data = await practiceApi.acceptMedCardDisclaimer(signature.trim());

      if (data.success) {
        setDisclaimerChecked(true);
        setShowDisclaimer(false);
        toast({
          title: "Disclaimer Accepted",
          description: "You can now access Med Cards",
        });
      }
    } catch (error) {
      console.error('Error accepting disclaimer:', error);
      toast({
        title: "Error",
        description: "Failed to accept disclaimer",
        variant: "destructive"
      });
    } finally {
      setDisclaimerLoading(false);
    }
  };

  const loadMedCards = async () => {
    try {
      const data = await practiceApi.getMedCards();
      if (data.success) {
        setMedCards(data.data);
      }
    } catch (error) {
      console.error('Error loading Med Cards:', error);
      toast({
        title: "Error",
        description: "Failed to load Med Cards",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const data = await practiceApi.getPatients();
      if (data.success) {
        setPatients(data.data);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  };

  const filterMedCards = () => {
    let filtered = [...medCards];

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(card => card.category === selectedCategory);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(card =>
        card.name.toLowerCase().includes(searchLower) ||
        card.category.toLowerCase().includes(searchLower)
      );
    }

    // Sort alphabetically
    filtered.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredMedCards(filtered);
  };

  const filterPatients = () => {
    let filtered = [...patients];

    // Filter by search - name, email, or phone
    if (patientSearchQuery.trim()) {
      const searchLower = patientSearchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
        (patient.email && patient.email.toLowerCase().includes(searchLower)) ||
        (patient.cellPhone && patient.cellPhone.includes(patientSearchQuery)) ||
        (patient.phone && patient.phone.includes(patientSearchQuery))
      );
    }

    // Sort alphabetically
    filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });

    setFilteredPatients(filtered);
  };

  const handleView = (card) => {
    setSelectedCard(card);
    setEditedContent(card.customContent || '');
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (card) => {
    setSelectedCard(card);
    // Pre-populate with baseContent so dentist can edit the original text
    setEditedContent(card.customContent || card.baseContent || '');
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCard) return;

    try {
      await practiceApi.updateMedCard(selectedCard.id, editedContent);

      toast({
        title: "Saved",
        description: "Med Card customizations saved to your library",
      });

      // Reload med cards to show updated version
      await loadMedCards();
      setShowModal(false);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving Med Card:', error);
      toast({
        title: "Error",
        description: "Failed to save Med Card",
        variant: "destructive"
      });
    }
  };

  const handlePrint = (card) => {
    const cardToPrint = card || selectedCard;
    if (!cardToPrint) return;
    
    // Create print content HTML with same formatting as view
    const createPrintContent = () => {
      const practice_logo = practice?.logoUrl ? `<img src="${practice.logoUrl}" alt="Practice Logo" style="height: 60px; margin-bottom: 15px;" />` : "";
      const practice_name = practice?.name || "Dental Practice";
      const practice_address = practice?.address ? `<p style="margin: 5px 0;">${practice.address.street || ""}, ${practice.address.city || ""}, ${practice.address.state || ""} ${practice.address.zipCode || ""}</p>` : "";
      const practice_phone = practice?.phone ? `<p style="margin: 5px 0; font-weight: bold;">Phone: ${practice.phone}</p>` : "";
      
      // Parse content into sections (same logic as view)
      const content = cardToPrint.customContent || cardToPrint.baseContent || '';
      const lines = content.split('\n');
      const sections = [];
      let currentSection = { title: '', content: [] };
      
      lines.forEach((line) => {
        const trimmed = line.trim();
        
        // Detect section headers
        if (trimmed.match(/^(Why this medicine|How much to take|How to take it|Important reminders|Common side effects|Call your dentist|Provider notice)/i)) {
          if (currentSection.title || currentSection.content.length > 0) {
            sections.push({...currentSection});
          }
          currentSection = { title: trimmed, content: [] };
        } else if (trimmed) {
          currentSection.content.push(trimmed);
        }
      });
      
      if (currentSection.title || currentSection.content.length > 0) {
        sections.push(currentSection);
      }
      
      // Filter out Provider Notice sections
      const patientSections = sections.filter(section => 
        !section.title.toLowerCase().includes('provider notice')
      );
      
      // Create sections HTML
      const sectionsHtml = patientSections.map((section) => {
        const title = section.title.toLowerCase();
        let bgColor = '#f9fafb';
        let borderColor = '#e5e7eb';
        let iconHtml = '📋';
        
        // Color code by section type (same as view)
        if (title.includes('important') || title.includes('do not')) {
          bgColor = '#fefce8';
          borderColor = '#fde047';
          iconHtml = '⚠️';
        } else if (title.includes('call') || title.includes('seek help')) {
          bgColor = '#fef2f2';
          borderColor = '#fca5a5';
          iconHtml = '🚨';
        } else if (title.includes('side effects')) {
          bgColor = '#fff7ed';
          borderColor = '#fed7aa';
          iconHtml = '⚠️';
        } else if (title.includes('how much')) {
          iconHtml = '💊';
        } else if (title.includes('why')) {
          iconHtml = '📋';
        }
        
        const contentHtml = section.content.map(line => {
          if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
            return `<p style="margin: 8px 0; display: flex; align-items: start;"><span style="color: #2563eb; font-weight: bold; margin-right: 8px;">•</span><span style="flex: 1;">${line.replace(/^[•\-*]\s*/, '')}</span></p>`;
          } else {
            return `<p style="margin: 8px 0;">${line}</p>`;
          }
        }).join('');
        
        return `
          <div style="margin-bottom: 16px;">
            ${section.title ? `
              <div style="background: #2563eb; color: white; padding: 10px 16px; margin-bottom: 8px; border-radius: 4px;">
                <h3 style="font-weight: bold; font-size: 16px; margin: 0; display: flex; align-items: center; gap: 8px;">
                  <span>${iconHtml}</span>
                  <span>${section.title}</span>
                </h3>
              </div>
            ` : ''}
            <div style="background: ${bgColor}; padding: 16px; border-radius: 4px; color: #374151; line-height: 1.6;">
              ${contentHtml}
            </div>
          </div>
        `;
      }).join('');
      
      return `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
          <!-- Practice Header -->
          <div style="text-align: center; margin-bottom: 20px; padding: 20px; border: 2px solid #e5e7eb; border-radius: 8px;">
            ${practice_logo}
            <h1 style="margin: 0; font-size: 20px; color: #1f2937;">${practice_name}</h1>
            ${practice_address}
            ${practice_phone}
          </div>
          
          <!-- Med Card Header -->
          <div style="background: linear-gradient(to right, #2563eb, #1d4ed8); color: white; padding: 20px; margin-bottom: 20px; border-radius: 8px; display: flex; align-items: center; justify-content: space-between;">
            <div>
              <h2 style="margin: 0 0 4px 0; font-size: 24px; font-weight: bold;">${cardToPrint.name || 'Medication Card'}</h2>
              <p style="margin: 0; opacity: 0.9; font-size: 14px;">${cardToPrint.category || ''}</p>
            </div>
            <div style="background: rgba(255,255,255,0.2); padding: 8px; border-radius: 8px;">
              <span style="font-size: 32px;">📋</span>
            </div>
          </div>
          
          <!-- Content Sections -->
          <div style="margin-bottom: 20px;">
            ${sectionsHtml}
          </div>
        </div>
      `;
    };
    
    // Remove existing print area
    const existingPrintArea = document.querySelector('.print-only');
    if (existingPrintArea) {
      existingPrintArea.remove();
    }
    
    // Create new print area
    const printArea = document.createElement('div');
    printArea.className = 'print-only';
    printArea.innerHTML = createPrintContent();
    document.body.appendChild(printArea);
    
    // If modal not open, open it first to get the data
    if (card && !showModal) {
      setSelectedCard(card);
      setIsEditing(false);
      setShowModal(true);
      setTimeout(() => {
        window.print();
      }, 100);
    } else {
      window.print();
    }
  };

  const handleEmailClick = (card) => {
    setEmailingCard(card);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!selectedPatient || !emailingCard) {
      toast({
        title: "Error",
        description: "Please select a patient",
        variant: "destructive"
      });
      return;
    }

    try {
      const patientName = `${selectedPatient.firstName} ${selectedPatient.lastName}`;
      const data = await practiceApi.emailMedCard(
        emailingCard.id,
        selectedPatient.email,
        patientName
      );
      
      if (data.success) {
        toast({
          title: "Email Sent",
          description: `Med Card "${emailingCard.name}" sent to ${patientName} at ${selectedPatient.email}`,
        });
        
        setShowEmailModal(false);
        setSelectedPatient(null);
        setEmailingCard(null);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Error",
        description: "Failed to send email. Please try again.",
        variant: "destructive"
      });
    }
  };

  // SMS functionality
  const handleSMSClick = (card) => {
    setSmsCard(card);
    setShowSMSModal(true);
  };

  const handleSendSMS = async () => {
    if (!smsPatient || !smsCard) {
      toast({
        title: "Error",
        description: "Please select a patient",
        variant: "destructive"
      });
      return;
    }

    const phoneNumber = smsPatient.cellPhone || smsPatient.phone;
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "This patient doesn't have a phone number on file.",
        variant: "destructive"
      });
      return;
    }

    try {
      setSendingSMS(true);
      const patientName = `${smsPatient.firstName} ${smsPatient.lastName}`;
      
      // Use the SMS API endpoint
      const token = localStorage.getItem('dentalToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/send-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: smsPatient.id,
          document_type: 'medcard',
          document_id: smsCard.id,
          phone_number: phoneNumber
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Text Sent",
          description: `Med Card "${smsCard.name}" texted to ${patientName}`,
        });
        
        setShowSMSModal(false);
        setSmsPatient(null);
        setSmsCard(null);
        setSmsPatientSearchQuery('');
      } else {
        throw new Error(data.detail || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send text. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingSMS(false);
    }
  };

  // Get unique categories
  const categories = ['All', ...new Set(medCards.map(card => card.category).filter(Boolean))];

  // Group cards by category for display
  const groupedCards = filteredMedCards.reduce((acc, card) => {
    const category = card.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(card);
    return acc;
  }, {});

  if (checkingDisclaimer || (loading && disclaimerChecked)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{printStyles}</style>
      <div className="min-h-screen bg-gray-50">
        {/* Disclaimer Modal */}
        <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-yellow-600" />
              Med Cards Provider Disclaimer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg space-y-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Purpose & Scope</h4>
                <p>DAN Med Cards are patient-education templates to support clinician-patient communication. They are not medical advice, treatment directives, or a substitute for professional judgment.</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Professional Review Required</h4>
                <p>Before distribution, the prescribing dentist must review and, as needed, edit each handout to confirm the correct drug, strength, route, adult/pediatric dosing, timing/duration, and patient-specific adjustments.</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Not a Prescription</h4>
                <p>These materials are not a prescription, medication order, or authorization to dispense.</p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Provider Responsibility</h4>
                <p>You are solely responsible for verifying accuracy and for all clinical decisions.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Digital Signature</label>
              <Input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Type your full name to accept"
              />
            </div>

            <Button 
              onClick={handleAcceptDisclaimer} 
              disabled={disclaimerLoading || !signature.trim()}
              className="w-full"
            >
              {disclaimerLoading ? 'Accepting...' : 'Accept & Continue'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto print-content">
          <DialogHeader className="no-print">
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedCard?.name}</span>
              {selectedCard?.isCustomized && (
                <Badge className="bg-blue-100 text-blue-800">Customized</Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 print-content">
            {/* Practice Header - Always Visible */}
            <div className="bg-white p-6 mb-4 border-2 border-gray-200 rounded-lg print-header">
              <div className="text-center mb-4">
                {practice?.logoUrl && (
                  <img src={practice.logoUrl} alt="Practice Logo" className="h-16 mx-auto mb-4" />
                )}
                <h1 className="text-xl font-bold text-gray-900">{practice?.name || 'Dental Practice'}</h1>
                {practice?.address && (
                  <p className="text-sm text-gray-600">
                    {practice.address.street}, {practice.address.city}, {practice.address.state} {practice.address.zipCode}
                  </p>
                )}
                {practice?.phone && (
                  <p className="text-sm text-gray-700 font-medium mt-1">Phone: {practice.phone}</p>
                )}
              </div>
            </div>

            {isEditing ? (
              <div className="space-y-4 no-print">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Edit Medication Instructions
                  </label>
                  <p className="text-xs text-gray-600 mb-2">
                    Edit the medication instructions below. Your changes will be saved to your practice library and used for all future patients.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
                    <p className="text-xs text-yellow-800">
                      <strong>⚠️ Note:</strong> Any "Provider Notice" sections will be automatically removed from patient-facing cards (print/email). These are for your reference only.
                    </p>
                  </div>
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm leading-relaxed"
                    placeholder="Edit medication instructions..."
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedContent(selectedCard?.customContent || '');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit}>
                    Save to Library
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Base Medication Instructions - Professionally Formatted */}
                {selectedCard?.baseContent && (
                  <div className="bg-white border-2 border-gray-300 rounded-xl shadow-sm">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-2xl font-bold mb-1">{selectedCard.name}</h2>
                          <p className="text-blue-100 text-sm">{selectedCard.category}</p>
                        </div>
                        <div className="bg-white/20 px-4 py-2 rounded-lg">
                          <FileText className="h-8 w-8" />
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-8 space-y-6">
                      {(() => {
                        const content = selectedCard.baseContent;
                        const sections = [];
                        
                        // Parse sections from content
                        const lines = content.split('\n');
                        let currentSection = { title: '', content: [] };
                        
                        lines.forEach((line, index) => {
                          const trimmed = line.trim();
                          
                          // Detect section headers
                          if (trimmed.match(/^(Why this medicine|How much to take|How to take it|Important reminders|Common side effects|Call your dentist|Provider notice)/i)) {
                            if (currentSection.title || currentSection.content.length > 0) {
                              sections.push({...currentSection});
                            }
                            currentSection = { title: trimmed, content: [] };
                          } else if (trimmed) {
                            currentSection.content.push(trimmed);
                          }
                        });
                        
                        if (currentSection.title || currentSection.content.length > 0) {
                          sections.push(currentSection);
                        }
                        
                        // CRITICAL: Filter out "Provider Notice" - this is for dentists ONLY, not patients
                        const patientSections = sections.filter(section => 
                          !section.title.toLowerCase().includes('provider notice')
                        );
                        
                        return patientSections.map((section, idx) => {
                          const title = section.title.toLowerCase();
                          let bgColor = 'bg-gray-50';
                          let borderColor = 'border-gray-200';
                          let textColor = 'text-gray-900';
                          let iconColor = 'text-blue-600';
                          
                          // Color code by section type
                          if (title.includes('important') || title.includes('do not')) {
                            bgColor = 'bg-yellow-50';
                            borderColor = 'border-yellow-300';
                            iconColor = 'text-yellow-600';
                          } else if (title.includes('call') || title.includes('seek help')) {
                            bgColor = 'bg-red-50';
                            borderColor = 'border-red-300';
                            iconColor = 'text-red-600';
                          } else if (title.includes('side effects')) {
                            bgColor = 'bg-orange-50';
                            borderColor = 'border-orange-200';
                            iconColor = 'text-orange-600';
                          } else if (title.includes('provider notice')) {
                            bgColor = 'bg-blue-50';
                            borderColor = 'border-blue-200';
                            textColor = 'text-gray-700';
                            iconColor = 'text-blue-600';
                          }
                          
                          return (
                            <div key={idx} className={`${bgColor} border-l-4 ${borderColor} rounded-r-lg p-5`}>
                              <div className="flex items-start space-x-3">
                                <div className={`${iconColor} mt-1 flex-shrink-0`}>
                                  {title.includes('why') && <FileText className="h-5 w-5" />}
                                  {title.includes('how much') && <span className="text-lg font-bold">💊</span>}
                                  {title.includes('how to take') && <span className="text-lg">📋</span>}
                                  {title.includes('important') && <AlertCircle className="h-5 w-5" />}
                                  {title.includes('side effects') && <span className="text-lg">⚠️</span>}
                                  {title.includes('call') && <span className="text-lg">🚨</span>}
                                  {title.includes('provider') && <span className="text-lg">ℹ️</span>}
                                </div>
                                <div className="flex-1">
                                  {section.title && (
                                    <h3 className={`font-bold text-lg mb-2 ${textColor}`}>
                                      {section.title}
                                    </h3>
                                  )}
                                  <div className="space-y-2 text-gray-800 leading-relaxed">
                                    {section.content.map((line, lineIdx) => (
                                      <p key={lineIdx} className="text-sm">
                                        {line.startsWith('•') || line.startsWith('-') || line.startsWith('*') ? (
                                          <span className="flex items-start space-x-2">
                                            <span className="text-blue-600 font-bold mt-0.5">•</span>
                                            <span className="flex-1">{line.replace(/^[•\-*]\s*/, '')}</span>
                                          </span>
                                        ) : (
                                          line
                                        )}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Custom Instructions (if any) */}
                {selectedCard?.customContent && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 shadow-sm">
                    <div className="flex items-start space-x-3">
                      <div className="bg-blue-600 text-white p-2 rounded-lg">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-blue-900 text-lg mb-3">Practice-Specific Custom Instructions</h4>
                        <div className="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed bg-white/50 p-4 rounded-lg">
                          {selectedCard.customContent}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 no-print">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditedContent(selectedCard?.customContent || selectedCard?.baseContent || '');
                      setIsEditing(true);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handlePrint()}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      handleEmailClick(selectedCard);
                    }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                  <Button
                    onClick={() => {
                      setShowModal(false);
                      handleSMSClick(selectedCard);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Text
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Patient Modal */}
      <Dialog open={showEmailModal} onOpenChange={(open) => {
        setShowEmailModal(open);
        if (!open) {
          setSelectedPatient(null);
          setPatientSearchQuery('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email Med Card to Patient</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a patient to email <strong>{emailingCard?.name}</strong>
            </p>

            {/* Patient Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                value={patientSearchQuery}
                onChange={(e) => setPatientSearchQuery(e.target.value)}
                placeholder="Search patients by name or email..."
                className="pl-10"
              />
            </div>

            {/* Patient List */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredPatients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">
                    {patientSearchQuery ? 'No patients found matching your search' : 'No patients available'}
                  </p>
                </div>
              ) : (
                filteredPatients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className={`p-3 border rounded-lg cursor-pointer transition ${
                      selectedPatient?.id === patient.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-xs text-gray-600">{patient.email}</p>
                      </div>
                      {selectedPatient?.id === patient.id && (
                        <Check className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEmailModal(false);
                  setSelectedPatient(null);
                  setPatientSearchQuery('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={!selectedPatient}
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Patient Modal */}
      <Dialog open={showSMSModal} onOpenChange={(open) => {
        setShowSMSModal(open);
        if (!open) {
          setSmsPatient(null);
          setSmsPatientSearchQuery('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2 text-green-600" />
              Text Med Card to Patient
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a patient to text <strong>{smsCard?.name}</strong>
            </p>

            {/* Patient Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                value={smsPatientSearchQuery}
                onChange={(e) => setSmsPatientSearchQuery(e.target.value)}
                placeholder="Search patients by name or phone..."
                className="pl-10"
              />
            </div>

            {/* Patient List */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredSmsPatients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Phone className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">
                    {smsPatientSearchQuery 
                      ? 'No patients with mobile numbers match your search' 
                      : 'No patients with mobile numbers available'}
                  </p>
                  <p className="text-xs mt-1">Only patients with phone numbers can receive text messages</p>
                </div>
              ) : (
                filteredSmsPatients.map((patient) => {
                  const hasPhone = patient.cellPhone || patient.phone;
                  return (
                    <div
                      key={patient.id}
                      onClick={() => setSmsPatient(patient)}
                      className={`p-3 border rounded-lg transition cursor-pointer ${
                        smsPatient?.id === patient.id
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p className="text-xs text-gray-600 flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {patient.cellPhone || patient.phone}
                          </p>
                        </div>
                        {smsPatient?.id === patient.id && (
                          <Check className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSMSModal(false);
                  setSmsPatient(null);
                  setSmsPatientSearchQuery('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendSMS}
                disabled={!smsPatient || sendingSMS}
                className="bg-green-600 hover:bg-green-700"
              >
                {sendingSMS ? (
                  <>Sending...</>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Send Text
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Return to Dashboard</span>
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Med Card Library</h1>
                <p className="text-gray-600">Medication instruction cards for patients</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Important:</strong> These are template documents. You must customize them for your practice and have them reviewed by your legal counsel before use.
          </AlertDescription>
        </Alert>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Med Cards..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-2 overflow-x-auto">
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  className="whitespace-nowrap"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Results count */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredMedCards.length} of {medCards.length} Med Cards
            {searchQuery && ` for "${searchQuery}"`}
          </div>
        </div>
      </div>

      {/* Med Cards Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {Object.keys(groupedCards).length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Med Cards Found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedCards).map(([category, cards]) => (
              <div key={category}>
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  {category}
                  <Badge variant="secondary" className="ml-3">{cards.length}</Badge>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {cards.map((card) => (
                    <Card key={card.id} className="hover:shadow-lg transition cursor-pointer group">
                      <CardHeader>
                        <CardTitle className="text-base flex items-start justify-between">
                          <span className="flex-1">{card.name}</span>
                          {card.isCustomized && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">Custom</Badge>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(card)}
                              className="flex-1"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(card)}
                              className="flex-1"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                          <div className="flex items-center justify-between">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePrint(card)}
                              className="flex-1"
                            >
                              <Printer className="h-4 w-4 mr-1" />
                              Print
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEmailClick(card)}
                              className="flex-1"
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              Email
                            </Button>
                          </div>
                          <div className="flex items-center justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSMSClick(card)}
                              className="flex-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Text
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default MedCardLibraryPage;
